import { isBefore } from "date-fns";
import { confirm } from "devextreme/ui/dialog";
import { IDBPDatabase } from "idb";
import {
  ApiCtx,
  call,
  Next,
  Ok,
  select,
  sleep,
  takeEvery,
  takeLeading,
} from "starfx";

import { dateToSqlDate, ensureArray } from "@app/service/helpers";
import {
  TFiscalBook,
  TFiscalBookToDb,
} from "@app/statefx/schema/fiscalBooks.def";

import { db } from "../../stateCommon/idbSchema";
import { isErr, isOk, safeLast } from "../helpers";
import { runThunk } from "../helpers/fxhelpers";
import { schema } from "../schema";
import { selectorWorkerGxDBName } from "../selectors/app.sel";
import { normalizeCIF } from "../selectors/uiFiscalBooks.sel";
import { xApi, xThunk, xWorker } from "../xapis";
import { sfxNotify } from "./notify";

import type { AppState, ThunkCtx } from "@app/types";
import type { IdProp } from "starfx";
function* parseableCtx(ctx: ApiCtx) {
  yield* sleep(1);
  return ctx.json;
}
export function mapDocumentSerialToDatabase(
  docSerial: TFiscalBook
): TFiscalBookToDb {
  const {
    FCT_FF,
    PREFIX,
    NUMARCIFRE,
    SUFIX,
    NUMAR,
    EMITENT,
    CIF_EMITENT,
    CHITANTA,
    CHELTUIELI,
    ESTE_CURENT,
    ALIAS,
    EXPLICATII,
    ALIAS_BIFAT,
    ACTIV_DELA,
    INACTIV_DELA,
    AMA_ID,
    NOTA,
  } = docSerial;

  return {
    FCT_FF,
    PREFIX,
    NUMARCIFRE,
    SUFIX,
    NUMAR,
    EMITENT,
    CIF_EMITENT,
    CHITANTA,
    CHELTUIELI,
    ESTE_CURENT,
    ALIAS,
    EXPLICATII,
    ALIAS_BIFAT,
    ACTIV_DELA,
    INACTIV_DELA,
    AMA_ID,
    NOTA,
  };
}

// [{NEWID:2}]

type TFiscalBookResponse = {
  NEWID: number;
};

type TDeleteResponse = {
  COD_OPERATIE: number;
};

/* 
  FCT_FF NUMERIC(10, 0),
  PREFIX VARCHAR(50),
  NUMARCIFRE NUMERIC(10, 0),
  SUFIX VARCHAR(50),
  NUMAR NUMERIC(10, 0),
  EMITENT VARCHAR(4),
  CIF_EMITENT VARCHAR(10),
  CHITANTA SMALLINT,
  CHELTUIELI INTEGER,
  ESTE_CURENT INTEGER,
  ALIAS VARCHAR(100),
  EXPLICATII VARCHAR(10),
  ALIAS_BIFAT INTEGER,
  ACTIV_DELA DATE,  -- CAMP ADAUGAT ASTAZI
  INACTIV_DELA DATE,  -- CAMP ADAUGAT ASTAZI
  AMA_ID INTEGER, --- CAMP ADAUGAT ASTAZI
  ERROR_MESSAGE VARCHAR(255)
  DENUMIRE_EMITENT VARCHAR(100)
*/
import type { ObjOutput, Operation, TableOutput } from "starfx";
export function* ReadIDB(): Operation<IDBPDatabase<unknown>> {
  const indexDbName = yield* select(selectorWorkerGxDBName);
  if (!indexDbName) {
    return null;
  }
  const t = yield* call(() => db(indexDbName));
  return t;
}

/// a cache fallback-recovery proof of concept
//// I guess not that useful in the real world
function* persistFromCache(
  objectName: keyof AppState,
  idx: string
): Operation<unknown> {
  let existent;
  let mapped;
  if (schema[objectName].schema === "table") {
    // - we hydrate the sfx from persistor. so we have already the last state
    existent = yield* select(
      (schema[objectName] as TableOutput<any, any>).selectTableAsList
    );
  } else if (schema[objectName].schema === "obj") {
    existent = yield* select(
      (schema[objectName] as ObjOutput<any, any>).select
    );
  } else {
    return;
  }
  // if not hydrated
  if (!Object.keys(existent).length) {
    const db = yield* ReadIDB();
    if (db) {
      let dataset = yield* call(() => db.getAll(`${objectName}`));
      if (dataset) {
        dataset = ensureArray(dataset);
        mapped = dataset.reduce((acc, item) => {
          acc[idx] = item;
          return acc;
        }, {});
        // we recovered the data from the cache
        console.log("we recovered the data from the cache,  accounts", mapped);
        schema[objectName].schema === "table"
          ? yield* schema.update(
              (schema[objectName] as TableOutput<any, any>).set(mapped)
            )
          : yield* schema.update(
              (schema[objectName] as ObjOutput<any, any>).set(mapped)
            );
      } else {
        console.log("we have no data in the cache");
      }
    }
  } else {
    console.log("we already have the data in the store");
  }
  return mapped || existent;
}

export const getfiscalBooks = xWorker.get(
  "/wait/sfx/select/fiscalBooks",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    ctx.request = ctx.req({
      url: "/select/fiscalBooks",
      method: "GET",
    });

    /// POC - a nonsense //
    /// const existent = yield* call(persistFromCache('fiscalBooks', 'FCT_FF'));
    ///

    yield* next();
    const data = yield* safeLast(parseableCtx(ctx));
    if (isErr(data)) {
      console.log("ctx.result", ctx.result);
      return;
    }
    if (isOk(data)) {
      const fiscalBooks = data.value as TFiscalBook[];
      // We set up a special field for alerting if an inconsistency is found
      const errMsg = fiscalBooks.find((item) => {
        return Boolean(item.ERROR_MESSAGE);
      });
      if (errMsg) {
        console.log("Error processing request:", errMsg);
        // yield* runThunk(sfxNotify, {
        //   type: 'error',
        //   message:
        //     errMsg?.ERROR_MESSAGE ||
        //     'Eroare de structura la seriile de facturi. Se incarca doar datele corecte.',
        //   duration: 10_000,
        // });
        // yield* sleep(10_000);
      }

      // const snew = sum(fiscalBooks);
      // const sold = sum(existent);
      // if (snew === sold) {
      //   //we save a map and a render //
      //   return;
      // }

      const mapped = fiscalBooks.reduce((acc, item) => {
        acc[item.FCT_FF] = item;
        return acc;
      }, {});

      yield* schema.update(schema.fiscalBooks.set(mapped));
    }
  }
);

/* 
export type TFiscalBook = {
  FCT_FF: number;
  PREFIX: string;
  NUMARCIFRE: number;
  SUFIX: string;
  NUMAR: number;
  EMITENT: string;
  CIF_EMITENT: string;
  CHITANTA: number;
  CHELTUIELI: number;
  ESTE_CURENT: number;
  ALIAS: string;
  EXPLICATII: string;
  ALIAS_BIFAT: number;
  ACTIV_DELA: string;
  INACTIV_DELA: string;
  AMA_ID: number;
  ERROR_MESSAGE: string;
  EXEMPLU?: string;
};

*/

// Utility function to identify duplicates with active status
const identifyActiveDuplicates = (
  dataset: Record<number, TFiscalBook>
): Record<string, number> => {
  return Object.values(dataset).reduce((acc, item) => {
    if (
      isBefore(new Date(item.ACTIV_DELA), new Date()) &&
      (!item.INACTIV_DELA || isBefore(new Date(), new Date(item.INACTIV_DELA)))
    ) {
      const key = `CIF:${normalizeCIF(item.CIF_EMITENT)} ${
        item.CHITANTA ? "CHITANTIERE ACTIVE" : "FACTURIERE ACTIVE"
      }`;
      acc[key] = (acc[key] || 0) + 1;
    }
    return acc;
  }, {});
};

// Function to calculate total count of active duplicates
const calculateActiveDuplicatesCount = (
  dataset: Record<IdProp, TFiscalBook>
): number => {
  return Object.values(identifyActiveDuplicates(dataset)).reduce(
    (total, count) => (count > 1 ? total + count : total),
    0
  );
};

// Function to generate a message for active duplicates
const generateActiveDuplicatesMessage = (
  dataset: Record<number, TFiscalBook>
): string => {
  return Object.entries(identifyActiveDuplicates(dataset)).reduce(
    (message, [key, value]) => {
      return value > 1
        ? `${message}${message ? ", " : ""}${key} (${value})`
        : message;
    },
    ""
  );
};

// Saga function to insert or update a serial number
export const sfxInsertOrUpdateSerialNumber = xApi.post<TFiscalBook>(
  "/sfx/assets/serialNumbers",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    const { FCT_FF } = ctx.payload;
    const existing = yield* select((s) =>
      schema.fiscalBooks.selectById(s, FCT_FF)
    );
    const payloadData =
      FCT_FF > 0 ? { ...existing, ...ctx.payload } : ctx.payload;

    const fullDatasetBeforeUpdate = yield* select(
      schema.fiscalBooks.selectTable
    );
    const fullDatasetAfterUpdate = {
      ...fullDatasetBeforeUpdate,
      [FCT_FF]: payloadData,
    };

    const duplicatesBeforeUpdate = calculateActiveDuplicatesCount(
      fullDatasetBeforeUpdate
    );
    const duplicatesAfterUpdate = calculateActiveDuplicatesCount(
      fullDatasetAfterUpdate
    );
    const reducesDuplicates = duplicatesAfterUpdate < duplicatesBeforeUpdate;
    if (!reducesDuplicates) {
      const duplicateMessage = generateActiveDuplicatesMessage(
        fullDatasetAfterUpdate
      );
      if (duplicateMessage) {
        const userConfirmed = yield* call(() =>
          confirm(
            `<h5>OPERATIUNE NERECOMANDATA.</h5><div> Se vor genera elemente active multiple :${duplicateMessage} <div><i>CONTINUATI?</i>`,
            "Exista deja Facturier/Chitantier activ"
          )
        );

        if (!userConfirmed) {
          // yield* next(); // << no
          return;
        }
      }
    }
    const payload_ = {
      ...payloadData,
      ACTIV_DELA: dateToSqlDate(new Date(payloadData.ACTIV_DELA)),
      INACTIV_DELA: payloadData.INACTIV_DELA
        ? dateToSqlDate(new Date(payloadData.INACTIV_DELA))
        : null,
    } as TFiscalBook;
    const payloadDb = mapDocumentSerialToDatabase(payload_);
    ctx.request = ctx.req({
      url: "/select/fiscalBooks",
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({ data: payloadDb }),
    });
    yield* next();
    const response = yield* safeLast(parseableCtx(ctx));
    if (isErr(response)) {
      console.log("Error processing request:", ctx.result);
      return;
    }
    if (isOk(response)) {
      const newIdAo = response.value as [TFiscalBookResponse];
      const newId = newIdAo[0].NEWID;
      /*
        NEWID = -1; -- No Emitent with this CIF and ABR
        NEWID = -2; -- Already exists
        if NEWID > 0 then ok we should update the record with this new id
      */
      if (newId === -1) {
        yield* runThunk(sfxNotify, {
          type: "error",
          message: "Nu exista Emitent cu acest CIF si Cod ABR",
          duration: 10_000,
        });
        return;
      }
      if (newId === -2) {
        yield* runThunk(sfxNotify, {
          type: "error",
          message: "Exista deja o inregistrare cu aceste date",
          duration: 10_000,
        });
        return;
      }
      const updated = { ...payload_, FCT_FF: newId };
      yield* schema.update(
        schema.fiscalBooks.patch({ [updated.FCT_FF]: updated })
      );
    }
  }
);

export const sfxDeleteSerialNumber = xApi.delete<{ FCT_FF: number }>(
  "/sfx/assets/serialNumbers",
  { supervisor: takeLeading },
  function* (ctx: ApiCtx, next: Next) {
    const { FCT_FF } = ctx.payload;
    ctx.request = ctx.req({
      url: "/select/fiscalBooks",
      headers: {
        "Content-Type": "application/json",
      },
      method: "DELETE",
      body: JSON.stringify({ data: { FCT_FF } }),
    });
    yield* next();
    const response = yield* safeLast(parseableCtx(ctx));
    if (isErr(response)) {
      console.log("Error processing request:", ctx.result);
      return;
    }
    if (isOk(response)) {
      /* 
      
        0,-1 : dm.Dialog('Eroare','Facturierul selectat nu exista.');
        -2 : dm.Dialog('Eroare','Pe acest facturier sunt facturi tiparite. Procedura nu poate fi efectuata.');
        -3 : dm.Dialog('Eroare','Pe acest facturier sunt chitante tiparite. Procedura nu poate fi efectuata.');
        Else     dm.Dialog('','Facturierul a fost sters.',4);
    
      */

      const deleted_ = response.value as TDeleteResponse;
      const deleted = deleted_?.[0].COD_OPERATIE;
      if (deleted === 0 || deleted === -1) {
        yield* runThunk(sfxNotify, {
          type: "error",
          message: "Facturierul selectat nu exista.",
          duration: 4_000,
        });
        return;
      }
      if (deleted === -2) {
        yield* runThunk(sfxNotify, {
          type: "error",
          message:
            "Pe acest facturier sunt facturi tiparite. Procedura nu poate fi efectuata.",
          duration: 4_000,
        });
        return;
      }
      if (deleted === -3) {
        yield* runThunk(sfxNotify, {
          type: "error",
          message:
            "Pe acest facturier sunt chitante tiparite. Procedura nu poate fi efectuata.",
          duration: 4_000,
        });
        return;
      }

      if (deleted > 0) {
        yield* schema.update(schema.fiscalBooks.remove([FCT_FF]));
        yield* call(getfiscalBooks.run());
        yield* runThunk(sfxNotify, {
          type: "success",
          message: "Facturierul a fost sters.",
          duration: 4_000,
        });
      }
    }
  }
);
export const sleepAndReturnBoolenTrue = xThunk.create<number>(
  "sleepAndReturnBoolenTrue",
  { supervisor: takeEvery },
  function* (ctx: ThunkCtx, next: Next) {
    yield* sleep(ctx?.payload || 1000);
    yield* next();
    ctx.result = Ok(true);
  }
);
